import type { VideoStyle } from '../../types';
import { Card } from '../ui';

export interface StyleSelectorProps {
    selectedStyle: VideoStyle;
    onStyleChange: (style: VideoStyle) => void;
}

const styles: Array<{
    id: VideoStyle;
    name: string;
    description: string;
    icon: JSX.Element;
}> = [
        {
            id: 'modern',
            name: 'Modern',
            description: 'Hurtige klip, energisk',
            icon: (
                <svg viewBox="0 0 24 24" fill="none">
                    <path d="M13 2L3 14H12L11 22L21 10H12L13 2Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
            ),
        },
        {
            id: 'elegant',
            name: 'Elegant',
            description: 'Blød, sofistikeret',
            icon: (
                <svg viewBox="0 0 24 24" fill="none">
                    <path d="M12 2C12 2 8 6 8 12C8 16 10 18 12 18C14 18 16 16 16 12C16 6 12 2 12 2Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                    <path d="M12 18V22" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
                </svg>
            ),
        },
        {
            id: 'dynamic',
            name: 'Dynamic',
            description: 'Hurtig, zoom effekter',
            icon: (
                <svg viewBox="0 0 24 24" fill="none">
                    <path d="M13 2L3 14H12L11 22L21 10H12L13 2Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                    <circle cx="18" cy="5" r="3" stroke="currentColor" strokeWidth="2" />
                </svg>
            ),
        },
        {
            id: 'cinematic',
            name: 'Cinematic',
            description: 'Dramatisk, episk',
            icon: (
                <svg viewBox="0 0 24 24" fill="none">
                    <rect x="2" y="7" width="20" height="10" rx="2" stroke="currentColor" strokeWidth="2" />
                    <path d="M7 7V17M12 7V17M17 7V17" stroke="currentColor" strokeWidth="2" />
                </svg>
            ),
        },
        {
            id: 'minimalist',
            name: 'Minimalist',
            description: 'Ren, professionel',
            icon: (
                <svg viewBox="0 0 24 24" fill="none">
                    <rect x="3" y="3" width="18" height="18" rx="2" stroke="currentColor" strokeWidth="2" />
                    <path d="M3 9H21M9 3V21" stroke="currentColor" strokeWidth="2" />
                </svg>
            ),
        },
    ];

export function StyleSelector({ selectedStyle, onStyleChange }: StyleSelectorProps) {
    return (
        <aside className="w-80 bg-gray-800/50 backdrop-blur-lg border-l border-gray-700 flex flex-col">
            {/* Header */}
            <div className="p-6 border-b border-gray-700">
                <h2 className="text-xl font-bold mb-1">Video Stilart</h2>
                <p className="text-sm text-gray-400">Vælg en æstetisk stil</p>
            </div>

            {/* Style List */}
            <div className="flex-1 overflow-y-auto scrollbar-thin p-4">
                <div className="space-y-3">
                    {styles.map((style) => (
                        <button
                            key={style.id}
                            onClick={() => onStyleChange(style.id)}
                            className={`
                w-full text-left transition-all duration-200
                ${selectedStyle === style.id ? 'scale-105' : 'hover:scale-102'}
              `}
                        >
                            <Card
                                variant={selectedStyle === style.id ? 'glass' : 'default'}
                                padding="md"
                                className={`
                  transition-all duration-200
                  ${selectedStyle === style.id
                                        ? 'ring-2 ring-primary-500 shadow-lg shadow-primary-500/20'
                                        : 'hover:border-gray-600'
                                    }
                `}
                            >
                                <div className="flex items-start gap-4">
                                    {/* Icon */}
                                    <div className={`
                    w-12 h-12 rounded-lg flex items-center justify-center flex-shrink-0
                    ${selectedStyle === style.id
                                            ? 'bg-primary-600/20 text-primary-400'
                                            : 'bg-gray-700 text-gray-400'
                                        }
                  `}>
                                        <div className="w-6 h-6">
                                            {style.icon}
                                        </div>
                                    </div>

                                    {/* Info */}
                                    <div className="flex-1 min-w-0">
                                        <h3 className="font-semibold mb-1">{style.name}</h3>
                                        <p className="text-sm text-gray-400">{style.description}</p>
                                    </div>

                                    {/* Selected Indicator */}
                                    {selectedStyle === style.id && (
                                        <div className="flex-shrink-0">
                                            <svg className="w-5 h-5 text-primary-400" fill="currentColor" viewBox="0 0 20 20">
                                                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                                            </svg>
                                        </div>
                                    )}
                                </div>
                            </Card>
                        </button>
                    ))}
                </div>
            </div>
        </aside>
    );
}
